import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Skeleton } from '@crestrewards/ui';

import useAuth from '../hooks/useAuth';

/**
 * Gate for everything behind sign-in.
 *
 * While the session is being restored we render the shell's shape as skeletons
 * rather than a spinner, and rather than flashing the login page at a user who
 * is in fact signed in.
 */
export default function ProtectedRoute() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'restoring') {
    return (
      <div className="flex min-h-screen bg-background" aria-busy="true" aria-live="polite">
        <span className="sr-only">Restoring your session</span>

        <div className="hidden w-60 shrink-0 border-r-hairline border-border-chrome bg-card p-4 lg:block">
          <Skeleton className="h-8 w-36" />
          <div className="mt-8 space-y-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        </div>

        <div className="flex-1">
          <div className="flex h-14 items-center justify-between border-b-hairline border-border-chrome bg-card px-6">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
          <div className="space-y-4 p-6">
            <Skeleton className="h-7 w-56" />
            <Skeleton className="h-4 w-80" />
            <Skeleton className="h-40 w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (status !== 'authenticated') {
    // `from` lets a future enhancement return the user where they were headed.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}
